/**
 * Web-side wrappers around the three parent-issue-link callables:
 *
 *   - linkPhaseToBacklogParentIssue        — append one parent-issue link
 *   - unlinkPhaseFromBacklogParentIssue    — remove one parent-issue link
 *   - syncBacklogParentIssueNow            — sync one entry or all entries
 *
 * The parent-issue analogue of `backlogMilestoneLink.ts`; mirrors the
 * same test-runner-injection pattern so vitest can stub the round-trip
 * without touching `firebase/functions`.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './firebase';

export interface LinkPhaseToBacklogParentIssueBody {
  projectId: string;
  phaseId: string;
  backlogProjectKey: string;
  parentIssueKey: string;
  forceSystems?: string[];
}

export type LinkPhaseToBacklogParentIssueResult =
  | {
      ok: true;
      parentIssue: {
        id: number;
        key: string;
        summary: string;
        projectKey: string;
        projectName: string;
      };
      added: string[];
      unchanged: string[];
      totalChildIssues: number;
      truncated: boolean;
    }
  | {
      ok: false;
      reason:
        | 'config-missing'
        | 'secret-error'
        | 'backlog-error'
        | 'phase-not-found'
        | 'parent-issue-not-found'
        | 'invalid-project-key';
      message: string;
    };

export interface UnlinkPhaseFromBacklogParentIssueBody {
  projectId: string;
  phaseId: string;
  backlogProjectKey: string;
  parentIssueId: number;
  removeImportedItems?: boolean;
}

export type UnlinkPhaseFromBacklogParentIssueResult =
  | { ok: true; cleared: boolean; removedItemCount: number; clearedItemCount: number }
  | { ok: false; reason: 'phase-not-found' | 'not-linked'; message: string };

export interface SyncBacklogParentIssueNowBody {
  projectId: string;
  phaseId: string;
  /** Identify a single entry. Omit BOTH to sync every entry on the phase. */
  backlogProjectKey?: string;
  parentIssueId?: number;
}

export interface PerParentEntrySyncOutcome {
  backlogProjectKey: string;
  parentIssueId: number;
  ok: boolean;
  added: string[];
  removed: string[];
  unchanged: string[];
  totalChildIssues: number;
  truncated: boolean;
  reason?: 'backlog-error' | 'parent-issue-not-found';
  message?: string;
}

export type SyncBacklogParentIssueNowResult =
  | {
      ok: true;
      entries: PerParentEntrySyncOutcome[];
      added: string[];
      removed: string[];
      unchanged: string[];
      totalChildIssues: number;
      truncated: boolean;
    }
  | {
      ok: false;
      reason: 'phase-not-found' | 'not-linked' | 'backlog-error';
      message: string;
    };

export type ParentLinkRunner = (
  body: LinkPhaseToBacklogParentIssueBody,
) => Promise<LinkPhaseToBacklogParentIssueResult>;
export type ParentUnlinkRunner = (
  body: UnlinkPhaseFromBacklogParentIssueBody,
) => Promise<UnlinkPhaseFromBacklogParentIssueResult>;
export type ParentSyncNowRunner = (
  body: SyncBacklogParentIssueNowBody,
) => Promise<SyncBacklogParentIssueNowResult>;

let _linkRunner: ParentLinkRunner | null = null;
let _unlinkRunner: ParentUnlinkRunner | null = null;
let _syncRunner: ParentSyncNowRunner | null = null;

export function setParentLinkRunnerForTests(
  runner: ParentLinkRunner | null,
): ParentLinkRunner | null {
  const prev = _linkRunner;
  _linkRunner = runner;
  return prev;
}
export function setParentUnlinkRunnerForTests(
  runner: ParentUnlinkRunner | null,
): ParentUnlinkRunner | null {
  const prev = _unlinkRunner;
  _unlinkRunner = runner;
  return prev;
}
export function setParentSyncNowRunnerForTests(
  runner: ParentSyncNowRunner | null,
): ParentSyncNowRunner | null {
  const prev = _syncRunner;
  _syncRunner = runner;
  return prev;
}

function defaultLinkRunner(): ParentLinkRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      LinkPhaseToBacklogParentIssueBody,
      LinkPhaseToBacklogParentIssueResult
    >(fns, 'linkPhaseToBacklogParentIssue');
    const res = await callable(body);
    return res.data;
  };
}

function defaultUnlinkRunner(): ParentUnlinkRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      UnlinkPhaseFromBacklogParentIssueBody,
      UnlinkPhaseFromBacklogParentIssueResult
    >(fns, 'unlinkPhaseFromBacklogParentIssue');
    const res = await callable(body);
    return res.data;
  };
}

function defaultSyncNowRunner(): ParentSyncNowRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      SyncBacklogParentIssueNowBody,
      SyncBacklogParentIssueNowResult
    >(fns, 'syncBacklogParentIssueNow');
    const res = await callable(body);
    return res.data;
  };
}

export async function linkPhaseToBacklogParentIssue(
  body: LinkPhaseToBacklogParentIssueBody,
): Promise<LinkPhaseToBacklogParentIssueResult> {
  const run = _linkRunner ?? defaultLinkRunner();
  return await run(body);
}

export async function unlinkPhaseFromBacklogParentIssue(
  body: UnlinkPhaseFromBacklogParentIssueBody,
): Promise<UnlinkPhaseFromBacklogParentIssueResult> {
  const run = _unlinkRunner ?? defaultUnlinkRunner();
  return await run(body);
}

export async function syncBacklogParentIssueNow(
  body: SyncBacklogParentIssueNowBody,
): Promise<SyncBacklogParentIssueNowResult> {
  const run = _syncRunner ?? defaultSyncNowRunner();
  return await run(body);
}
